import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { FlightProfile } from "../../core/decorFlights";

const STEP_RISE = 0.17;
const STEP_TREAD = 0.3;
const TOP_LANDING = 1.2;
const MIDDLE_LANDING = 2;
/** Longueur des paliers, en plus des marches. */
export const OPEN_STAIRS_LANDINGS = TOP_LANDING + MIDDLE_LANDING;
const SLAB = 0.35;
const BALUSTRADE_HEIGHT = 1;
const BALUSTRADE_WIDTH = 0.08;

const concrete = new THREE.MeshStandardMaterial({ name: "concrete", color: "#bdbab4", roughness: 0.9, metalness: 0 });
const nosing = new THREE.MeshStandardMaterial({ name: "nosing", color: "#5d5f63", roughness: 0.8, metalness: 0.1 });
const glass = new THREE.MeshStandardMaterial({
  name: "glass",
  color: "#cfe6f0",
  roughness: 0.1,
  metalness: 0,
  transparent: true,
  opacity: 0.35,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const handrail = new THREE.MeshStandardMaterial({ name: "handrail", color: "#202326", roughness: 0.5, metalness: 0.2 });

function box(width: number, height: number, depth: number, x: number, y: number, z: number, tiltX = 0): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  if (tiltX) geometry.rotateX(tiltX);
  geometry.translate(x, y, z);
  return geometry;
}

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

function stepCount(rise: number): number {
  return Math.max(2, Math.round(rise / STEP_RISE));
}

/** Hauteur de l'escalier le long de sa descente, paliers compris : voir `FlightProfile`. */
export function openStairsProfile(rise: number, tread = STEP_TREAD): FlightProfile {
  const steps = stepCount(rise);
  const upper = Math.ceil(steps / 2);
  const middleY = rise - upper * (rise / steps);
  const middle = TOP_LANDING + upper * tread;
  return [[0, rise], [TOP_LANDING, rise], [middle, middleY], [middle + MIDDLE_LANDING, middleY], [openStairsLength(rise, tread), 0]];
}

/** Longueur au sol d'un escalier en plein air de ce dénivelé, paliers compris. */
export function openStairsLength(rise: number, tread = STEP_TREAD): number {
  return OPEN_STAIRS_LANDINGS + stepCount(rise) * tread;
}

/**
 * Escalier posé à l'air libre, du sol jusqu'à `rise` mètres plus haut, en deux volées séparées par
 * un palier : on y entre côté −z par le palier du haut, il descend vers +z jusqu'au sol. Mesures en
 * mètres.
 */
export function buildOpenStairs(width: number, rise: number, tread = STEP_TREAD): THREE.Group {
  const steps = stepCount(rise);
  const stepRise = rise / steps;
  const upper = Math.ceil(steps / 2);
  const middleY = rise - upper * stepRise;
  const z0 = -openStairsLength(rise, tread) / 2;
  const flightStart = [z0 + TOP_LANDING, z0 + TOP_LANDING + upper * tread + MIDDLE_LANDING];
  const middleZ = z0 + TOP_LANDING + upper * tread;

  const blocks: THREE.BufferGeometry[] = [
    box(width, SLAB, TOP_LANDING, 0, rise - SLAB / 2, z0 + TOP_LANDING / 2),
    box(width, SLAB, MIDDLE_LANDING, 0, middleY - SLAB / 2, middleZ + MIDDLE_LANDING / 2),
  ];
  const noses: THREE.BufferGeometry[] = [];
  for (let i = 0; i < steps; i++) {
    const top = rise - (i + 1) * stepRise;
    const z = (i < upper ? flightStart[0]! + i * tread : flightStart[1]! + (i - upper) * tread);
    const height = Math.min(top + stepRise, stepRise + SLAB);
    blocks.push(box(width, height, tread, 0, top + stepRise - height / 2, z + tread / 2));
    noses.push(box(width, 0.01, 0.05, 0, top + stepRise + 0.005, z + 0.025));
  }

  // Garde-corps : une section par palier, une par volée, inclinée comme elle.
  const sections: { y: number; z: number; length: number; tilt: number }[] = [
    { y: rise, z: z0 + TOP_LANDING / 2, length: TOP_LANDING, tilt: 0 },
    { y: middleY, z: middleZ + MIDDLE_LANDING / 2, length: MIDDLE_LANDING, tilt: 0 },
  ];
  for (const [start, count, fromY] of [[flightStart[0]!, upper, rise], [flightStart[1]!, steps - upper, middleY]] as const) {
    const run = count * tread;
    const drop = count * stepRise;
    const tilt = Math.atan2(drop, run);
    // Raccourcie de ce dont ses coins dépassent une fois inclinée.
    sections.push({ y: fromY - drop / 2, z: start + run / 2, length: Math.hypot(run, drop) - BALUSTRADE_HEIGHT * Math.sin(tilt), tilt });
  }
  const panels: THREE.BufferGeometry[] = [];
  const rails: THREE.BufferGeometry[] = [];
  for (const side of [-1, 1]) {
    const x = side * (width / 2 - BALUSTRADE_WIDTH / 2);
    for (const { y, z, length, tilt } of sections) {
      panels.push(box(0.03, BALUSTRADE_HEIGHT, length, x, y + BALUSTRADE_HEIGHT / 2, z, tilt));
      rails.push(box(BALUSTRADE_WIDTH, 0.06, length, x, y + BALUSTRADE_HEIGHT, z, tilt));
    }
  }

  const root = new THREE.Group();
  root.add(merged(blocks, concrete, "Steps"), merged(noses, nosing, "Nosing"), merged(panels, glass, "Glass"), merged(rails, handrail, "Handrails"));
  return root;
}
